/**
 * FX 交易结算纯函数
 *
 * <p>封装开仓、平仓、爆仓、借钱、还款、计息与权益计算。
 * 金额统一使用"分"（整数）存储，避免浮点误差。</p>
 */

/**
 * 创建初始账户状态
 *
 * @param {number} initialBalanceCents 初始资金（分）
 * @returns {object} 账户状态
 */
export function createAccount(initialBalanceCents) {
	return {
		balance: initialBalanceCents,
		debt: 0,
		interestPaid: 0,
		positions: [],
		nextPositionId: 1
	}
}

/**
 * 计算单笔持仓浮动盈亏（分）
 *
 * @param {object} position 持仓
 * @param {number} curPrice 当前价格
 * @returns {number} 浮动盈亏（分）
 */
export function computeUnrealizedPnl(position, curPrice) {
	const priceMove = position.direction === 'long'
		? (curPrice - position.openPrice) / position.openPrice
		: (position.openPrice - curPrice) / position.openPrice
	return Math.round(position.notionalCents * priceMove)
}

/**
 * 计算所有持仓浮动盈亏
 *
 * @param {object} account 账户状态
 * @param {Map<string, number>} priceMap 品种代码 → 当前价格
 * @returns {number} 总浮动盈亏（分）
 */
export function totalUnrealizedPnl(account, priceMap) {
	let total = 0
	for (const position of account.positions) {
		const price = priceMap.get(position.symbol)
		if (price != null) {
			total += computeUnrealizedPnl(position, price)
		}
	}
	return total
}

/**
 * 计算账户权益（分）= 余额 + 持仓保证金 + 浮动盈亏 - 负债
 *
 * <p>保证金在开仓时已从余额扣除，因此计算权益时需要加回，
 * 否则开仓瞬间会虚减权益并可能误触发破产判定。</p>
 *
 * @param {object} account 账户状态
 * @param {Map<string, number>} priceMap 品种代码 → 当前价格
 * @returns {number} 权益（分）
 */
export function getEquity(account, priceMap) {
	let marginTotal = 0
	for (const position of account.positions) {
		marginTotal += position.marginCents
	}
	return account.balance + marginTotal + totalUnrealizedPnl(account, priceMap) - account.debt
}

/**
 * 开仓
 *
 * <p>校验余额足够（保证金 + 手续费）后扣款并记录持仓。</p>
 *
 * @param {object} account 账户状态
 * @param {object} params 开仓参数
 * @param {string} params.symbol 品种代码
 * @param {string} params.direction 方向 long/short
 * @param {number} params.marginCents 保证金（分）
 * @param {number} params.leverage 杠杆倍数
 * @param {number} params.openPrice 开仓价格
 * @param {number} params.feeCents 手续费（分）
 * @returns {object} 新账户状态
 * @throws {Error} 余额不足时抛出
 */
export function openPosition(account, params) {
	const { symbol, direction, marginCents, leverage, openPrice, feeCents } = params
	if (!Number.isInteger(marginCents) || marginCents <= 0) {
		throw new Error('投入金额无效')
	}
	if (direction !== 'long' && direction !== 'short') {
		throw new Error('交易方向无效')
	}
	const cost = marginCents + feeCents
	if (account.balance < cost) {
		throw new Error('余额不足，无法开仓')
	}
	const nextPositionId = account.nextPositionId
	const position = {
		id: nextPositionId,
		symbol,
		direction,
		openPrice,
		marginCents,
		notionalCents: marginCents * leverage,
		leverage
	}
	return {
		...account,
		nextPositionId: nextPositionId + 1,
		balance: account.balance - cost,
		positions: [...account.positions, position]
	}
}

/**
 * 平仓指定持仓
 *
 * <p>返还保证金并结算浮动盈亏。</p>
 *
 * @param {object} account 账户状态
 * @param {number} positionId 持仓 ID
 * @param {number} curPrice 当前价格
 * @returns {object} 新账户状态
 */
export function closePosition(account, positionId, curPrice) {
	const index = account.positions.findIndex(p => p.id === positionId)
	if (index === -1) {
		throw new Error('持仓不存在')
	}
	const position = account.positions[index]
	const pnl = computeUnrealizedPnl(position, curPrice)
	const positions = account.positions.slice()
	positions.splice(index, 1)
	return {
		...account,
		balance: account.balance + position.marginCents + pnl,
		positions
	}
}

/**
 * 检查并执行爆仓
 *
 * <p>浮动亏损达到保证金时强制平仓（保证金亏光，返回 0）。</p>
 *
 * @param {object} account 账户状态
 * @param {Map<string, number>} priceMap 品种代码 → 当前价格
 * @returns {{account: object, liquidated: Array}} 新账户状态与爆仓持仓列表
 */
export function liquidatePositions(account, priceMap) {
	let liquidated = []
	let positions = account.positions
	for (let i = positions.length - 1; i >= 0; i--) {
		const position = positions[i]
		const price = priceMap.get(position.symbol)
		if (price == null) {
			continue
		}
		const pnl = computeUnrealizedPnl(position, price)
		if (pnl <= -position.marginCents) {
			liquidated.push({ ...position, pnl })
			positions = positions.slice()
			positions.splice(i, 1)
		}
	}
	if (liquidated.length === 0) {
		return { account, liquidated }
	}
	return {
		account: { ...account, positions },
		liquidated
	}
}

/**
 * 借钱
 *
 * @param {object} account 账户状态
 * @param {number} amountCents 借入金额（分）
 * @param {number} maxCents 最大可借金额（分）
 * @returns {object} 新账户状态
 * @throws {Error} 超过上限或金额无效时抛出
 */
export function borrow(account, amountCents, maxCents) {
	if (!Number.isInteger(amountCents) || amountCents <= 0) {
		throw new Error('借入金额无效')
	}
	if (account.debt + amountCents > maxCents) {
		throw new Error('超过最大可借金额')
	}
	return {
		...account,
		balance: account.balance + amountCents,
		debt: account.debt + amountCents
	}
}

/**
 * 还款
 *
 * <p>还款额不超过余额与负债的最小值。</p>
 *
 * @param {object} account 账户状态
 * @param {number} amountCents 还款金额（分）
 * @returns {object} 新账户状态
 */
export function repay(account, amountCents) {
	if (!Number.isInteger(amountCents) || amountCents <= 0) {
		throw new Error('还款金额无效')
	}
	const actual = Math.max(0, Math.min(amountCents, account.balance, account.debt))
	return {
		...account,
		balance: account.balance - actual,
		debt: account.debt - actual
	}
}

/**
 * 根据负债计算当日利率
 *
 * @param {number} debtCents 负债（分）
 * @param {Array<{limit: number, rate: number}>} tiers 利率分级（limit 为美元金额）
 * @returns {number} 当日利率（小数）
 */
export function getRateForDebt(debtCents, tiers) {
	const debtDollars = debtCents / 100
	let rate = 0
	for (const tier of tiers) {
		if (debtDollars <= tier.limit) {
			rate = tier.rate
			break
		}
		rate = tier.rate
	}
	return rate
}

/**
 * 结算一日利息（余额扣除）
 *
 * @param {object} account 账户状态
 * @param {Array<{limit: number, rate: number}>} tiers 利率分级
 * @returns {object} 新账户状态
 */
export function accrueInterest(account, tiers) {
	if (account.debt <= 0) {
		return account
	}
	const rate = getRateForDebt(account.debt, tiers)
	const interestCents = Math.round(account.debt * rate)
	return {
		...account,
		balance: account.balance - interestCents,
		interestPaid: account.interestPaid + interestCents
	}
}

/**
 * 重置账户为初始状态
 *
 * @param {number} initialBalanceCents 初始资金（分）
 * @returns {object} 新账户状态
 */
export function resetAccount(initialBalanceCents) {
	return createAccount(initialBalanceCents)
}
